import { useState, useEffect } from "react";
import { authFetch, API, useAuth } from "@/App";
import { Trophy, TrendingUp, Target, Users, IndianRupee, ChevronUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const formatCurrency = (paise) => {
  const r = (paise || 0) / 100;
  if (r >= 1e7) return `₹${(r / 1e7).toFixed(2)}Cr`;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(2)}L`;
  if (r >= 1e3) return `₹${(r / 1e3).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
};

const Spark = ({ series, color = "#8B1A1A" }) => {
  const w = 220, h = 56;
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((s) => s.count));
  const pts = series
    .map((s, i) => `${(i / (series.length - 1)) * w},${h - (s.count / max) * (h - 6) - 3}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="w-full h-14" preserveAspectRatio="none" viewBox={`0 0 ${w} ${h}`}>
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill="url(#spark-fill)" />
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts} />
      {series.map((s, i) => (
        <circle key={i} cx={(i / (series.length - 1)) * w} cy={h - (s.count / max) * (h - 6) - 3} r="1.5" fill={color} opacity={i === series.length - 1 ? 1 : 0.4} />
      ))}
    </svg>
  );
};

/**
 * MyPerformancePage — personalised dashboard for a salesman: rank, sparkline,
 * gap-to-leader and a peer top-3.  Powered by GET /api/mobile/me/performance.
 */
const MyPerformancePage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    authFetch(`${API}/mobile/me/performance?days=${days}`)
      .then((r) => r.json())
      .then((d) => mounted && setData(d.success ? d.data : null))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [days]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 spinner" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-500">No performance data yet — place a few orders this week and check back!</div>;

  const isLeader = data.my_rank === 1 && data.my_orders_count > 0;
  const accentColor = isLeader ? "#F59E0B" : "#8B1A1A";

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="my-performance-page">
      {/* Hero */}
      <Card className="p-5 dark:bg-slate-800 overflow-hidden relative" data-testid="hero-rank">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${isLeader ? "bg-gradient-to-br from-amber-400 to-orange-500" : "bg-gradient-to-br from-[#8B1A1A] to-rose-700"}`}>
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Your Rank · last {data.period_days} days</p>
            <p className="text-3xl font-bold dark:text-white">
              #{data.my_rank || "—"}
              <span className="text-lg text-slate-400 font-normal ml-1">/ {data.total_in_region}</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {user?.name || data.name} · Region {data.region_id || "All India"}
            </p>
          </div>
          {isLeader && (
            <Badge className="bg-amber-500 text-white shrink-0 animate-pulse">👑 Leader</Badge>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Your Orders</div>
            <p className="text-2xl font-bold font-mono dark:text-white">{data.my_orders_count}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Your Revenue</div>
            <p className="text-2xl font-bold font-mono dark:text-white">{formatCurrency(data.my_revenue)}</p>
          </div>
        </div>

        {/* Sparkline */}
        <div className="mt-5">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span>Daily orders trend</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-6 text-[10px] border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded px-1"
              data-testid="period-select"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </p>
          <Spark series={data.sparkline} color={accentColor} />
        </div>
      </Card>

      {/* Gap to leader */}
      {!isLeader && data.leader_orders_count > 0 && (
        <Card className="p-4 dark:bg-slate-800" data-testid="gap-to-leader-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 dark:text-slate-400">Gap to the leader</p>
              <p className="text-base font-semibold dark:text-white">
                <span className="text-amber-600 dark:text-amber-400">{data.gap_to_leader_orders}</span> orders to overtake{" "}
                <span className="text-slate-700 dark:text-slate-200">{data.leader_name || "the leader"}</span>
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Leader: {data.leader_orders_count} orders · {formatCurrency(data.leader_revenue)} ·
                {" "}You're {Math.round(((data.my_orders_count || 0) / Math.max(1, data.leader_orders_count)) * 100)}% there
              </p>
            </div>
            <ChevronUp className="w-5 h-5 text-emerald-500" />
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#8B1A1A] to-rose-500 transition-all duration-700"
              style={{ width: `${Math.min(100, ((data.my_orders_count || 0) / Math.max(1, data.leader_orders_count)) * 100)}%` }}
            />
          </div>
        </Card>
      )}

      {/* Peer top 3 */}
      {data.top_3 && data.top_3.length > 0 && (
        <Card className="p-4 dark:bg-slate-800" data-testid="top-3-peers">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold dark:text-white">Top 3 in your region</h3>
          </div>
          <div className="space-y-2">
            {data.top_3.map((p) => (
              <div
                key={p.id}
                className={`flex items-center gap-3 p-2.5 rounded-lg ${p.is_me ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40" : "bg-slate-50 dark:bg-slate-900/40"}`}
                data-testid={`top-peer-${p.rank}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${p.rank === 1 ? "bg-amber-500" : p.rank === 2 ? "bg-slate-400" : "bg-orange-700"}`}>
                  {p.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium dark:text-white truncate">
                    {p.name} {p.is_me && <span className="text-amber-600 text-[10px] font-bold ml-1">YOU</span>}
                  </p>
                  {p.code && <p className="text-[10px] text-slate-400">{p.code}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold font-mono dark:text-white">{p.orders}</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">{formatCurrency(p.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MyPerformancePage;
